// Fetch wrapper con auth header injection · base url '/api'
// Vite proxy mapea /api → VITE_BACKEND_URL en dev (vite.config.ts).
// En prod, nginx hace routing /api/* → backend (mismo dominio).

import type {
  AchievementsResponse,
  ActivateLicenseBody,
  AdminSuggestionsPage,
  ApiError,
  AuthIdentity,
  BonusProgress,
  BonusProgressListResponse,
  BriefingHistoryResponse,
  BriefingTodayResponse,
  CommentLikeResponse,
  CommentsResponse,
  CompleteModuleResponse,
  CourseBonus,
  CourseBonusesResponse,
  CourseDetail,
  CoursesListResponse,
  CreatePlanPayload,
  DiscordChatEditResponse,
  DiscordChatMessage,
  DiscordChatMessagesResponse,
  DiscordChatPostResponse,
  DiscordChatStreamEvent,
  DiscordLinkInitResponse,
  DiscordLinkStatusResponse,
  DiscordLinkSyncResponse,
  DiscordLinkUnlinkResponse,
  EnrollCourseResponse,
  LessonNote,
  LessonProgressResponse,
  LicensesResponse,
  MarkAllReadResponse,
  ModuleComment,
  MyCoursesResponse,
  NotificationsResponse,
  NotifyCourseResponse,
  PlanWithCalculator,
  PlansListResponse,
  RecentCommentsResponse,
  ResourcesResponse,
  SignalStats,
  SuggestionCategory,
  SuggestionCreatedResponse,
  SuggestionPayload,
  SuggestionStatus,
  SuggestionStatusResponse,
  TutorMessagesResponse,
  TutorSendResponse,
  TutorSession,
  TutorSessionsResponse,
  UpdatePlanPayload,
  UserPlanV2,
  UserProgress,
  ChecklistState,
  UserPlanPayload,
} from './types';
import type { UserPlan } from './finance-projection';
import { isStubModeAllowed } from './env';
import { getImpersonationToken, type ImpersonateStartResponse } from './impersonation';
import type { CampusRole } from './permissions';

const API_BASE = '/api';

export function buildHeaders(auth: AuthIdentity | null): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Impersonación "ver como alumno" (F3): si hay un token de impersonación
  // activo, se usa como Bearer en TODAS las llamadas → el backend resuelve la
  // sesión como el alumno objetivo (read-only · bloquea escrituras). Tiene
  // PRIORIDAD sobre el JWT real del admin. El stop/expiración lo retira.
  const impersonationToken = getImpersonationToken();
  if (impersonationToken) {
    headers['Authorization'] = `Bearer ${impersonationToken}`;
    return headers;
  }
  if (!auth) {
    return headers;
  }
  if (auth.mode === 'supabase' && auth.jwt) {
    headers['Authorization'] = `Bearer ${auth.jwt}`;
  } else if (auth.mode === 'stub') {
    // F-AUDIT-MVP-001 · defense in depth: X-User-Email SOLO se emite
    // cuando `isStubModeAllowed()` (dev build AND VITE_AUTH_MODE=stub).
    // En producción esta rama no se ejecuta porque auth-context jamás
    // produce mode='stub'; este chequeo es redundante a propósito para
    // garantizar que ninguna ruta filtre el header al backend prod.
    if (isStubModeAllowed()) {
      headers['X-User-Email'] = auth.email;
    } else if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[api] stub identity ignored: X-User-Email NOT sent (no dev+stub mode)',
      );
    }
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.ok) {
    return (await res.json()) as T;
  }
  let detail = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { detail?: string };
    if (typeof body.detail === 'string') {
      detail = body.detail;
    }
  } catch {
    // ignore json parse errors
  }
  const err: ApiError = { detail, status: res.status };
  throw err;
}

export async function fetchProgress(auth: AuthIdentity | null): Promise<UserProgress> {
  const res = await fetch(`${API_BASE}/me/progress`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<UserProgress>(res);
}

export async function completeModule(
  slug: string,
  checklistState: ChecklistState,
  auth: AuthIdentity | null,
): Promise<CompleteModuleResponse> {
  const res = await fetch(`${API_BASE}/modules/${encodeURIComponent(slug)}/complete`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ checklist_state: checklistState }),
  });
  return handleResponse<CompleteModuleResponse>(res);
}

// ── Licensing (B2 / B3) ──────────────────────────────────────────────────────

export async function fetchLicenses(auth: AuthIdentity | null): Promise<LicensesResponse> {
  const res = await fetch(`${API_BASE}/me/licenses`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<LicensesResponse>(res);
}

/** Qué ficheros de cada robot existen de verdad en el backend: `slug → clave →
 *  bool` (claves `robot`, `real`, `preset-*` y la sintética `presets-zip`).
 *  El catálogo del frontend declara lo que DEBERÍA haber; esto dice lo que HAY.
 *  Sin esto la UI ofrece descargas que responden 404 al pulsarlas. */
export type RobotFileAvailability = Record<string, Record<string, boolean>>;

export async function fetchRobotAvailability(
  auth: AuthIdentity | null,
): Promise<RobotFileAvailability> {
  const res = await fetch(`${API_BASE}/me/robots/availability`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<RobotFileAvailability>(res);
}

/**
 * Descarga autenticada de un fichero binario (robot demo, preset…) desde un
 * endpoint que exige sesión. El fichero NO es una URL pública: se pide con el
 * JWT del alumno y se entrega como blob → descarga en el navegador. Así la IP
 * del robot reseller no queda expuesta en una URL accesible por cualquiera.
 */
export async function downloadAuthedFile(
  auth: AuthIdentity | null,
  url: string,
): Promise<void> {
  const res = await fetch(url, { method: 'GET', headers: buildHeaders(auth) });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      /* respuesta sin JSON · se mantiene el detalle por defecto */
    }
    const err: ApiError = { detail, status: res.status };
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : 'descarga';
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function activateLicense(
  body: ActivateLicenseBody,
  auth: AuthIdentity | null,
): Promise<unknown> {
  const res = await fetch(`${API_BASE}/me/licenses/activate`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(body),
  });
  return handleResponse<unknown>(res);
}

export async function revokeLicense(
  licenseId: string,
  auth: AuthIdentity | null,
): Promise<unknown> {
  const res = await fetch(`${API_BASE}/me/licenses/revoke`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ license_id: licenseId }),
  });
  return handleResponse<unknown>(res);
}

// ─── Mi Plan financiero · F1 sprint campus 7-day ────────────────────

interface PlanEnvelope {
  plan: UserPlan;
}

export async function getMyPlan(auth: AuthIdentity | null): Promise<UserPlan | null> {
  const res = await fetch(`${API_BASE}/me/plan`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  if (res.status === 404) return null;
  const body = await handleResponse<PlanEnvelope>(res);
  return body.plan;
}

export async function saveMyPlan(
  payload: UserPlanPayload,
  auth: AuthIdentity | null,
): Promise<UserPlan> {
  const res = await fetch(`${API_BASE}/me/plan`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(payload),
  });
  const body = await handleResponse<PlanEnvelope>(res);
  return body.plan;
}

export async function deleteMyPlan(auth: AuthIdentity | null): Promise<void> {
  const res = await fetch(`${API_BASE}/me/plan`, {
    method: 'DELETE',
    headers: buildHeaders(auth),
  });
  if (!res.ok && res.status !== 204) {
    await handleResponse<unknown>(res);
  }
}

// ─── Mi Plan multi-plan (Mi Plan redesign 2026-05-27) ──────────────────────
// Endpoints `/api/me/plans` · permite historial · 3 calculator types.

export async function fetchMyPlans(
  auth: AuthIdentity | null,
): Promise<UserPlanV2[]> {
  const res = await fetch(`${API_BASE}/me/plans`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  const body = await handleResponse<PlansListResponse>(res);
  return body.plans;
}

export async function fetchPlan(
  planId: string,
  auth: AuthIdentity | null,
): Promise<PlanWithCalculator> {
  const res = await fetch(
    `${API_BASE}/me/plans/${encodeURIComponent(planId)}`,
    {
      method: 'GET',
      headers: buildHeaders(auth),
    },
  );
  return handleResponse<PlanWithCalculator>(res);
}

export async function createPlan(
  payload: CreatePlanPayload,
  auth: AuthIdentity | null,
): Promise<PlanWithCalculator> {
  const res = await fetch(`${API_BASE}/me/plans`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(payload),
  });
  return handleResponse<PlanWithCalculator>(res);
}

export async function updatePlan(
  planId: string,
  patch: UpdatePlanPayload,
  auth: AuthIdentity | null,
): Promise<PlanWithCalculator> {
  const res = await fetch(
    `${API_BASE}/me/plans/${encodeURIComponent(planId)}`,
    {
      method: 'PATCH',
      headers: buildHeaders(auth),
      body: JSON.stringify(patch),
    },
  );
  return handleResponse<PlanWithCalculator>(res);
}

export async function deletePlan(
  planId: string,
  auth: AuthIdentity | null,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/me/plans/${encodeURIComponent(planId)}`,
    {
      method: 'DELETE',
      headers: buildHeaders(auth),
    },
  );
  if (!res.ok && res.status !== 204) {
    await handleResponse<unknown>(res);
  }
}

// ─── Admin ──────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  source: string;
  // ID de contacto en GO CRM (GHL). Presente si el usuario fue creado/sincronizado
  // desde GHL; null si se registró por otra vía (manual, regalo, etc.).
  ghl_contact_id: string | null;
  role: CampusRole;
  // Etiqueta GHL de compra más alta del alumno en el último sync GHL
  // (literal, ej. "rb - compro pro"). Null si nunca se sincronizó o sin tag.
  ghl_source_tag?: string | null;
  // course_slug del entitlement activo de mayor rango (elite>pro>basic>start)
  // o null si el usuario no tiene ninguno.
  tier?: string | null;
  // Estado de engagement por last_seen_at (activo <7d · riesgo 7-30d · dormido).
  status?: 'activo' | 'riesgo' | 'dormido' | null;
  activated_at: string | null;
  last_seen_at: string | null;
  created_at: string | null;
}

export interface AdminStats {
  users_total: number;
  admins_total: number;
  students_total: number;
  plans_total: number;
  // Titularidad de curso activa (campus.user_entitlements, revoked_at IS NULL)
  // por course_slug. Siempre incluye robotea-start/basic/pro (0 si nadie).
  entitlement_counts: Record<string, number>;
  // Último acceso global más reciente (ISO) + actividad reciente.
  last_seen_global: string | null;
  active_users_24h: number;
  active_users_7d: number;
  modules: Array<{
    slug: string;
    title: string;
    position: number;
    phase: string;
    started: number;
    completed: number;
  }>;
  recent_users: AdminUser[];
  user_progress: Array<{
    email: string;
    full_name: string | null;
    module_slug: string;
    module_title: string;
    module_position: number;
    completed: boolean;
    checklist_state: Record<string, boolean>;
  }>;
}

export async function fetchAdminStats(auth: AuthIdentity | null): Promise<AdminStats> {
  const res = await fetch(`${API_BASE}/admin/stats`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminStats>(res);
}

// ── Navegador de contenido por curso (read-only) ──
export interface AdminCourseLesson {
  id: string;
  position: number;
  title: string;
  section_title: string | null;
  has_video: boolean;
  duration_seconds: number | null;
}

export interface AdminCourseModule {
  id: number;
  slug: string;
  title: string;
  position: number;
  phase: string;
  short_description: string | null;
  lessons_count: number;
  lessons: AdminCourseLesson[];
}

export interface AdminCourseStructure {
  course: {
    id: number;
    slug: string;
    title: string;
    status: string;
  };
  modules: AdminCourseModule[];
}

export async function fetchAdminCourseStructure(
  slug: string,
  auth: AuthIdentity | null,
): Promise<AdminCourseStructure> {
  const res = await fetch(`${API_BASE}/admin/courses/${encodeURIComponent(slug)}/structure`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminCourseStructure>(res);
}

export async function fetchAdminUsers(
  auth: AuthIdentity | null,
  params: {
    q?: string;
    role?: string;
    status?: 'activo' | 'riesgo' | 'dormido';
    limit?: number;
    offset?: number;
  } = {},
): Promise<{ total: number; limit: number; offset: number; users: AdminUser[] }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.role) qs.set('role', params.role);
  if (params.status) qs.set('status', params.status);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const res = await fetch(`${API_BASE}/admin/users?${qs.toString()}`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<{ total: number; limit: number; offset: number; users: AdminUser[] }>(res);
}

export async function updateUserRole(
  userId: string,
  role: CampusRole,
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; user_id: string; email: string; role: string }> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify({ role }),
  });
  return handleResponse<{ ok: boolean; user_id: string; email: string; role: string }>(res);
}

export interface AdminUserAccessCourse {
  slug: string;
  title: string;
  ghl_purchase_tag: string;
  enabled: boolean;
}

export interface AdminUserAccess {
  user_id: string;
  user_email: string;
  ghl_contact_id: string | null;
  ghl_unavailable: boolean;
  courses: AdminUserAccessCourse[];
}

export async function fetchAdminUserAccess(
  userId: string,
  auth: AuthIdentity | null,
): Promise<AdminUserAccess> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/access`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminUserAccess>(res);
}

export async function patchAdminUserAccess(
  userId: string,
  courseSlug: string,
  enabled: boolean,
  auth: AuthIdentity | null,
): Promise<{
  ok: boolean;
  user_id: string;
  user_email: string;
  course_slug: string;
  enabled: boolean;
  ghl_tag: string;
}> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/access`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify({ course_slug: courseSlug, enabled }),
  });
  return handleResponse(res);
}

// ── Panel usuarios · grant/revoke explícitos + perfil + licensing ────────────

export async function grantAdminUserCourse(
  userId: string,
  courseSlug: string,
  auth: AuthIdentity | null,
  opts: { source?: 'gift' | 'manual'; note?: string } = {},
): Promise<{
  ok: boolean;
  user_id: string;
  user_email: string;
  course_slug: string;
  source: string;
  already_existed: boolean;
}> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/courses/grant`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({
      course_slug: courseSlug,
      source: opts.source ?? 'gift',
      note: opts.note,
    }),
  });
  return handleResponse(res);
}

export async function revokeAdminUserCourse(
  userId: string,
  courseSlug: string,
  auth: AuthIdentity | null,
): Promise<{
  ok: boolean;
  user_id: string;
  user_email: string;
  course_slug: string;
  revoked: boolean;
}> {
  const res = await fetch(
    `${API_BASE}/admin/users/${userId}/courses/${encodeURIComponent(courseSlug)}`,
    {
      method: 'DELETE',
      headers: buildHeaders(auth),
    },
  );
  return handleResponse(res);
}

export interface AdminUserDetail {
  id: string;
  email: string;
  full_name: string | null;
  source: string;
  role: CampusRole;
}

export async function patchAdminUser(
  userId: string,
  fields: { full_name?: string; email?: string; source?: string },
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; user: AdminUserDetail }> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify(fields),
  });
  return handleResponse(res);
}

// ── Auditoría / registro de actividad (Admin v2 · F1) ───────────────────────

export interface AdminAuditEvent {
  id: number;
  occurred_at: string | null;
  actor_type: 'admin' | 'user' | 'system' | 'integration';
  actor_id: string | null;
  actor_label: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  subject_user_id: string | null;
  subject_label: string | null;
  source: string | null;
  ip: string | null;
  metadata: Record<string, unknown>;
}

export interface AdminAuditPage {
  total: number;
  limit: number;
  offset: number;
  events: AdminAuditEvent[];
}

export async function fetchAdminAudit(
  auth: AuthIdentity | null,
  params: {
    action?: string;
    actorId?: string;
    subjectUserId?: string;
    source?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<AdminAuditPage> {
  const qs = new URLSearchParams();
  if (params.action) qs.set('action', params.action);
  if (params.actorId) qs.set('actor_id', params.actorId);
  if (params.subjectUserId) qs.set('subject_user_id', params.subjectUserId);
  if (params.source) qs.set('source', params.source);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const res = await fetch(`${API_BASE}/admin/audit?${qs.toString()}`, {
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminAuditPage>(res);
}

export async function fetchAdminAuditActions(
  auth: AuthIdentity | null,
): Promise<{ actions: string[] }> {
  const res = await fetch(`${API_BASE}/admin/audit/actions`, {
    headers: buildHeaders(auth),
  });
  return handleResponse<{ actions: string[] }>(res);
}

// ── Buzón de sugerencias (feat/buzon-nav-usuarios · 2026-06-15) ──────────────
// Feedback CONSTRUCTIVO sobre el campus. El POST lo hace cualquier autenticado;
// el GET/PATCH están gated server-side por P_SUGERENCIAS_VIEW / _MANAGE.

/** Envía una sugerencia del usuario autenticado (POST /api/me/suggestions). */
export async function submitSuggestion(
  payload: SuggestionPayload,
  auth: AuthIdentity | null,
): Promise<SuggestionCreatedResponse> {
  const res = await fetch(`${API_BASE}/me/suggestions`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(payload),
  });
  return handleResponse<SuggestionCreatedResponse>(res);
}

/** Bandeja admin de sugerencias (GET /api/admin/suggestions · filtrable). */
export async function listAdminSuggestions(
  auth: AuthIdentity | null,
  params: {
    status?: SuggestionStatus;
    category?: SuggestionCategory;
    limit?: number;
    offset?: number;
  } = {},
): Promise<AdminSuggestionsPage> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.category) qs.set('category', params.category);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const res = await fetch(`${API_BASE}/admin/suggestions?${qs.toString()}`, {
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminSuggestionsPage>(res);
}

/** Cambia el status de una sugerencia (PATCH /api/admin/suggestions/{id}). */
export async function updateSuggestionStatus(
  suggestionId: string,
  status: SuggestionStatus,
  auth: AuthIdentity | null,
): Promise<SuggestionStatusResponse> {
  const res = await fetch(
    `${API_BASE}/admin/suggestions/${encodeURIComponent(suggestionId)}`,
    {
      method: 'PATCH',
      headers: buildHeaders(auth),
      body: JSON.stringify({ status }),
    },
  );
  return handleResponse<SuggestionStatusResponse>(res);
}

// ── Vista 360º del alumno (Admin v2 · F2) ────────────────────────────────────
// Agregador read-only: reúne "con qué está conectado" un alumno. El backend
// degrada por sección (campo `degraded` lista las que fallaron) y excluye
// stripe ids/payload + bodies del tutor (mask-secretos + minimización).

export interface AdminUser360 {
  identity: {
    id: string;
    email: string;
    full_name: string | null;
    ghl_contact_id: string | null;
    source: string | null;
    role: CampusRole | null;
    activated_at: string | null;
    last_seen_at: string | null;
    created_at: string | null;
    onboarding_completed_at: string | null;
    first_login_at: string | null;
  };
  entitlements: Array<{
    course_slug: string;
    source: string | null;
    granted_by: string | null;
    granted_at: string | null;
    revoked_at: string | null;
    active: boolean;
    note: string | null;
  }>;
  rank: { rank: string; label: string } | null;
  progress: {
    modules_started: number;
    modules_completed: number;
    lessons_started: number;
    lessons_completed: number;
    modules_detail?: Array<{ module_id: number; module_title: string | null; completed_at: string | null }>;
    lessons_detail?: Array<{ lesson_id: string; lesson_title: string | null; completed_at: string | null }>;
  } | null;
  gamification: {
    total_xp: number;
    current_level: number;
    recent_events: Array<{
      event_kind: string;
      reference_id: string | null;
      xp_delta: number;
      created_at: string | null;
    }>;
  } | null;
  licenses: Array<{
    robot_code: string | null;
    robot_label: string | null;
    broker: string | null;
    account_number: number | null;
    platform: string | null;
    account_type: string | null;
    status: string | null;
    requested_at: string | null;
    reviewed_at: string | null;
    first_activated_at: string | null;
  }>;
  discord: {
    discord_user_id: string | null;
    discord_username: string | null;
    linked_at: string | null;
    last_synced_role: string | null;
    last_sync_at: string | null;
  } | null;
  emails: Array<{
    email_kind: string;
    reference_id: string | null;
    sent_at: string | null;
  }>;
  referrals: {
    my_code: string | null;
    converted_count: number;
    pending_count: number;
    total_count: number;
  } | null;
  purchases: Array<{
    product_code: string | null;
    product_title: string | null;
    status: string | null;
    amount_cents: number | null;
    currency: string | null;
    created_at: string | null;
    completed_at: string | null;
  }>;
  tutor: { total_sessions: number; last_session_at: string | null } | null;
  timeline: AdminAuditEvent[];
  degraded: string[];
}

export async function fetchAdminUser360(
  userId: string,
  auth: AuthIdentity | null,
): Promise<AdminUser360> {
  const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}/360`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminUser360>(res);
}

// ── Emails de ciclo de vida por alumno (feat/admin-emails-por-alumno) ────────

export interface AdminUserEmailSummaryItem {
  email_kind: string;
  label: string;
  received: boolean;
  count: number;
}

export interface AdminUserEmailRecord {
  email_kind: string;
  label: string;
  reference_id: string | null;
  sent_at: string | null;
}

export interface AdminUserEmails {
  user_id: string;
  total: number;
  onboarding_coverage: {
    received: number;
    total: number;
    complete: boolean;
  };
  summary: AdminUserEmailSummaryItem[];
  emails: AdminUserEmailRecord[];
}

export async function fetchAdminUserEmails(
  userId: string,
  auth: AuthIdentity | null,
): Promise<AdminUserEmails> {
  const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}/emails`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminUserEmails>(res);
}

// Estado de emails para todos los alumnos · una sola query agregada.
// { "<user_uuid>": { onboarding_d1: bool, onboarding_d3: bool, onboarding_d7: bool, reengage: bool } }
export interface AdminEmailBulkStatus {
  onboarding_d1: boolean;
  onboarding_d3: boolean;
  onboarding_d7: boolean;
  reengage: boolean;
}

export async function fetchAdminEmailsBulkStatus(
  auth: AuthIdentity | null,
): Promise<Record<string, AdminEmailBulkStatus>> {
  const res = await fetch(`${API_BASE}/admin/emails/bulk-status`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<Record<string, AdminEmailBulkStatus>>(res);
}

// ── Analítica del admin (Admin v2 · read-only) ───────────────────────────────

export interface AdminAnalyticsOverview {
  users_total: number;
  students_total: number;
  admins_total: number;
  by_tier: { start: number; basic: number; pro: number; elite: number };
  active_24h: number;
  active_7d: number;
  revenue_month_cents: number;
  degraded: string[];
}
export interface AdminAnalyticsFunnel {
  registrados: number;
  activados: number;
  con_progreso: number;
  con_licencia_real: number;
  degraded: string[];
}
export interface AdminEngagementUser {
  email: string;
  last_seen_at: string | null;
  tier: string | null;
}
export interface AdminAnalyticsEngagement {
  activo: number;
  riesgo: number;
  dormido: number;
  en_riesgo: AdminEngagementUser[];
  degraded: string[];
}
export interface AdminAnalyticsCohorts {
  weeks: Array<{ week: string | null; altas: number }>;
  degraded: string[];
}
export interface AdminAnalyticsRevenue {
  by_month: Array<{ month: string | null; total_cents: number; currency: string | null }>;
  by_product: Array<{ product_code: string | null; product_title: string | null; total_cents: number; count: number }>;
  degraded: string[];
}
export interface AdminAnalyticsCompletion {
  modules: Array<{
    module_id: number | string;
    module_title?: string | null;
    course_title?: string | null;
    started: number;
    completed: number;
    pct: number;
  }>;
  degraded: string[];
}

// Seguimiento Demo · una fila por usuario en demo (campus.users.demo_plan IS NOT NULL).
export interface DemoTrackingRow {
  user_id: number | string;
  email: string;
  full_name: string | null;
  registered_at: string | null;
  onboarding_done: boolean;
  fase2_done: boolean;
  fase2_at: string | null;
  has_demo: boolean;
  days_in_demo: number | null;
  fase3_done: boolean;
  fase3_at: string | null;
  real_license_requested_at: string | null;
  real_license_activated: boolean;
  is_real: boolean;
  demo_expires_at: string | null;
  demo_locked_at: string | null;
  days_remaining: number | null;
  wants_call: boolean;
  journey_emails: string[];
  journey_emails_count: number;
  bought_start: boolean;
  score: number;
}
export interface AdminAnalyticsDemoTracking {
  items: DemoTrackingRow[];
}

async function getAdmin<T>(path: string, auth: AuthIdentity | null): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: 'GET', headers: buildHeaders(auth) });
  return handleResponse<T>(res);
}

export const fetchAnalyticsOverview = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsOverview>('/admin/analytics', a);
export const fetchAnalyticsFunnel = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsFunnel>('/admin/analytics/funnel', a);
export const fetchAnalyticsEngagement = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsEngagement>('/admin/analytics/engagement', a);
export const fetchAnalyticsCohorts = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsCohorts>('/admin/analytics/cohorts', a);
export const fetchAnalyticsRevenue = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsRevenue>('/admin/analytics/revenue', a);
export const fetchAnalyticsCompletion = (a: AuthIdentity | null) => getAdmin<AdminAnalyticsCompletion>('/admin/analytics/completion', a);
export const fetchAnalyticsDemoTracking = (a: AuthIdentity | null) =>
  getAdmin<AdminAnalyticsDemoTracking>('/admin/analytics/demo-tracking', a);

// ── Salud de integraciones (Admin v2 · monitor) ──────────────────────────────

export interface AdminIntegrationHealth {
  name: string;
  ok: boolean;
  latency_ms: number | null;
  detail: string;
}
export interface AdminIntegrationsHealth {
  integrations: AdminIntegrationHealth[];
}

export const fetchIntegrationsHealth = (a: AuthIdentity | null) =>
  getAdmin<AdminIntegrationsHealth>('/admin/integrations/health', a);

export async function resyncIntegration(
  name: string,
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; detail: string }> {
  const res = await fetch(`${API_BASE}/admin/integrations/${encodeURIComponent(name)}/resync`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<{ ok: boolean; detail: string }>(res);
}

// ── Consumo de contenido (Admin v2 · read-only · por curso/módulo/lección) ───

export interface AdminContentConsumption {
  courses: Array<{
    course_slug: string;
    course_title: string;
    modules: Array<{ module_id: number; module_title: string; started: number; completed: number }>;
    lessons: Array<{ lesson_id: string; lesson_title: string; module_title: string; started: number; completed: number }>;
  }>;
  degraded: string[];
}
export const fetchAnalyticsContent = (a: AuthIdentity | null) =>
  getAdmin<AdminContentConsumption>('/admin/analytics/content', a);

export interface AdminCourseCompleters {
  courses: Array<{
    course_slug: string;
    course_title: string;
    modules_total: number;
    completed_count: number;
    users: Array<{ email: string; full_name: string | null }>;
  }>;
  degraded: string[];
}
export const fetchCourseCompleters = (a: AuthIdentity | null) =>
  getAdmin<AdminCourseCompleters>('/admin/analytics/completers', a);

// ── Licencias activas por usuario (Admin v2 · global · read-only) ────────────

export interface AdminActiveLicense {
  robot_code: string | null;
  robot_label: string | null;
  broker: string | null;
  account_number: number | null;
  platform: string | null;
  account_type: string | null;
  /** Fuente de la licencia (alumno/tienda/cortesia). Opcional: el backend
   *  puede no exponerlo todavía en /admin/licenses/active. */
  customer_type?: string | null;
  status: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  last_seen_at?: string | null;
}
export interface AdminLicensesActive {
  source?: string;
  active_statuses?: string[];
  total_licenses?: number;
  users: Array<{
    user_id?: string;
    email: string;
    full_name: string | null;
    licenses: AdminActiveLicense[];
  }>;
  degraded: string[];
}
export const fetchAdminLicensesActive = (a: AuthIdentity | null) =>
  getAdmin<AdminLicensesActive>('/admin/licenses/active', a);

// ── Gestión de licencias · espejo del Hub (F-LIC-CAMPUS-001 · 2026-06-16) ─────
// Proxy del Campus al licensing API del Hub. Misma data + acciones que las
// páginas de licencias del Hub, con estilo del Campus. ADITIVO: el Hub intacto.

async function postAdmin<T>(
  path: string,
  auth: AuthIdentity | null,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

async function patchAdmin<T>(
  path: string,
  auth: AuthIdentity | null,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

/** Una licencia tal cual la devuelve el Hub (/v1/admin/licenses). */
export interface HubLicense {
  id: string;
  customer_email: string;
  customer_name: string | null;
  customer_type: string;
  product_id: string;
  account_number: number | null;
  broker_server: string | null;
  status: string;
  issued_at: string | null;
  approved_by?: string | null;
  /** Nombre del staff aprobador · enriquecido por el backend desde campus.users. */
  approved_by_name?: string | null;
  approved_at?: string | null;
  expires_at?: string | null;
  last_seen_at?: string | null;
  account_platform?: string | null;
  account_type?: string | null;
  customer_telegram?: string | null;
  notes?: string | null;
  archived_at?: string | null;
  /** Fase 1 · pre-check IB: momento en que se confirmó la afiliación IB (API broker o staff). */
  ib_verified_at?: string | null;
  /** Quién verificó el IB: 'puprime-api' o email del staff. */
  ib_verified_by?: string | null;
  /** F-CAMPUS-SIMLIC-001: true si viene de `campus.robot_activations`
   *  (licensing_mode 'simulated'/'vendor') en vez de una licencia real del
   *  Hub. Nunca tiene HMAC del core — el frontend NO debe ofrecer sobre ella
   *  acciones de gestión del core (verify-ib/revoke/archive). */
  is_simulated?: boolean;
}

export interface HubStudent {
  customer_email: string;
  customer_name: string | null;
  customer_telegram?: string | null;
  fecha_ingreso?: string | null;
  /** Fecha de primera compra (ISO) · columna "Ingreso" de la pestaña Alumnos. */
  first_purchase_at?: string | null;
  /** El alumno es un tester interno (bypass de cuota). */
  is_tester?: boolean;
  n_licencias?: number;
  robots?: string[];
  brokers?: string[];
  labels?: string[];
  tier_max?: string;
}

export interface HubOrder {
  id: string;
  customer_email: string;
  customer_name?: string | null;
  product_id: string;
  qty_activations: number;
  slots_used?: number;
  source?: string;
  external_ref?: string | null;
  created_at?: string | null;
}

export interface HubProduct {
  id: string;
  name: string;
  /** Nombre para mostrar (si el catálogo lo trae · opcional). */
  display_name?: string | null;
  /** Tier del producto (start/basic/pro) · null para robots de tienda/adicionales. */
  tier?: string | null;
  /** Canales donde el producto está disponible (alumno/tienda/...). */
  available_for?: string[];
}

interface HubListEnvelope<T> {
  items: T[];
  degraded: string[];
}

export interface HubGhlHealth {
  available: boolean;
  has_api_key?: boolean;
  has_location_id?: boolean;
  degraded?: string[];
}

const HUB = '/admin/licensing';

export const fetchHubLicenses = (
  a: AuthIdentity | null,
  params: { status?: string; email?: string; name?: string; customer_type?: string; include_archived?: boolean } = {},
) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.email) qs.set('email', params.email);
  if (params.name) qs.set('name', params.name);
  if (params.customer_type) qs.set('customer_type', params.customer_type);
  if (params.include_archived) qs.set('include_archived', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getAdmin<HubListEnvelope<HubLicense>>(`${HUB}/licenses${suffix}`, a);
};

export const fetchHubStudents = (a: AuthIdentity | null) =>
  getAdmin<HubListEnvelope<HubStudent>>(`${HUB}/students`, a);

export const fetchHubOrders = (a: AuthIdentity | null) =>
  getAdmin<HubListEnvelope<HubOrder>>(`${HUB}/orders`, a);

/** Una quota de un tester (robot + cupo + activaciones reales). */
export interface HubTesterQuota {
  product_id: string;
  product_name: string;
  quota: number;
  active_count: number;
}

/** Un tester interno tal cual lo devuelve el Hub (sin `notes` · PII drop). */
export interface HubTester {
  id: string;
  customer_email: string;
  display_name: string | null;
  created_at?: string | null;
  created_by?: string | null;
  archived_at?: string | null;
  quotas: HubTesterQuota[];
}

export const fetchHubTesters = (
  a: AuthIdentity | null,
  params: { include_archived?: boolean } = {},
) => {
  const qs = params.include_archived ? '?include_archived=true' : '';
  return getAdmin<HubListEnvelope<HubTester>>(`${HUB}/testers${qs}`, a);
};

export const fetchHubProducts = (
  a: AuthIdentity | null,
  params: { available_for?: string } = {},
) => {
  const qs = params.available_for
    ? `?available_for=${encodeURIComponent(params.available_for)}`
    : '';
  return getAdmin<HubListEnvelope<HubProduct>>(`${HUB}/products${qs}`, a);
};

export const fetchHubGhlHealth = (a: AuthIdentity | null) =>
  getAdmin<HubGhlHealth>(`${HUB}/ghl/health`, a);

export const hubVerifyIb = (a: AuthIdentity | null, licenseId: string, notes?: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/verify-ib`, a, { notes: notes ?? null });

/** Fase 1 · marca "IB verificado" en una licencia core pendiente SIN activarla (VT/FP/Exness · manual). */
export const hubMarkIbVerified = (a: AuthIdentity | null, licenseId: string, notes?: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/mark-ib-verified`, a, { notes: notes ?? null });

/** Resultado de "Pedir cambio de IB" (Fase C · Telegram + email al alumno). */
export interface RequestIbChangeResult {
  ok: boolean;
  broker: string | null;
  mode: 'self' | 'email' | 'contact';
  telegram_linked: boolean;
  telegram_sent: boolean;
  email_sent: boolean;
}

/** Envía al alumno los pasos de cambio de IB de su broker por Telegram + email. */
export const requestIbChange = (
  a: AuthIdentity | null,
  licenseId: string,
  payload: { customer_email: string; broker: string | null },
) =>
  postAdmin<RequestIbChangeResult>(
    `${HUB}/licenses/${encodeURIComponent(licenseId)}/request-ib-change`,
    a,
    { customer_email: payload.customer_email, broker: payload.broker },
  );

export const hubRevoke = (a: AuthIdentity | null, licenseId: string, reason: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/revoke`, a, { reason });

export const hubArchive = (a: AuthIdentity | null, licenseId: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/archive`, a);

/** Reactivar (desarchivar) una licencia archivada · soft-undo del archive. */
export const hubUnarchive = (a: AuthIdentity | null, licenseId: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/unarchive`, a);

/** Eliminar una licencia de forma irreversible (hard-delete). */
export const hubDelete = (a: AuthIdentity | null, licenseId: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/${encodeURIComponent(licenseId)}/delete`, a);

export interface HubManualLicensePayload {
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
  product_id: string;
  account_number: number;
  broker_server: string;
  customer_telegram?: string | null;
  account_platform?: string | null;
  account_type?: string | null;
  customer_type?: string;
  activate_immediately?: boolean;
  notes?: string | null;
}
export const hubCreateManual = (a: AuthIdentity | null, payload: HubManualLicensePayload) =>
  postAdmin<Record<string, unknown>>(`${HUB}/licenses/create-manual`, a, payload);

export interface HubOrderPayload {
  customer_email: string;
  customer_name?: string | null;
  product_id: string;
  qty_activations: number;
  source?: string;
  external_ref?: string | null;
  notes?: string | null;
}
export const hubCreateOrder = (a: AuthIdentity | null, payload: HubOrderPayload) =>
  postAdmin<Record<string, unknown>>(`${HUB}/orders`, a, payload);

export const hubGhlSyncContact = (a: AuthIdentity | null, email: string) =>
  postAdmin<Record<string, unknown>>(`${HUB}/ghl/sync-contact?email=${encodeURIComponent(email)}`, a);

export const hubGhlSyncBatch = (a: AuthIdentity | null, limit = 200) =>
  postAdmin<Record<string, unknown>>(`${HUB}/ghl/sync-batch?limit=${limit}`, a);

export interface HubEntitlementSyncResult {
  email: string;
  found_tags: string[];
  granted: string[];
  already: string[];
  degraded: boolean;
}
export const hubSyncEntitlementsFromGhl = (a: AuthIdentity | null, email: string) =>
  postAdmin<HubEntitlementSyncResult>(`${HUB}/entitlements/sync-from-ghl?email=${encodeURIComponent(email)}`, a);

export const hubSyncEntitlementsFromGhlBatch = (a: AuthIdentity | null, limit = 200) =>
  postAdmin<{ processed: number; granted_total: number; degraded: number; changed: unknown[] }>(
    `${HUB}/entitlements/sync-from-ghl/batch?limit=${limit}`,
    a,
  );

// ── Gestión de licencias de ALUMNOS del campus (schema campus.*) ──────────────
// Capa NUEVA (spec 2026-06-20): configuración de comportamiento por robot
// (robot_plan_config) + activaciones gestionadas por el campus (robot_activations).
// NO toca el licensing core: es la gestión propia del campus.

/** Modo de aprobación de la versión demo de un robot. */
export type DemoApproval = 'auto_instant' | 'manual';
/** Modo de aprobación de la versión real de un robot. */
export type RealApproval = 'manual' | 'auto_instant' | 'auto_delayed';
/**
 * Modo de licencia del robot (F-CAMPUS-LICMODE-001). Gobierna si la activación
 * crea licencia en el core de licensing:
 *  · robotea   → licencia oficial Robotea (reseller/propio) · puente ON · EA valida.
 *  · vendor    → proveedor externo licencia el robot · puente OFF.
 *  · simulated → simulación de activación (auto-aprueba con delay) · puente OFF.
 */
export type LicensingMode = 'robotea' | 'vendor' | 'simulated';

/** Fila de configuración de comportamiento de un robot del plan. */
export interface RobotPlanConfig {
  product_id: string;
  product_label: string;
  tier: string;
  slot: string;
  demo_enabled: boolean;
  real_enabled: boolean;
  demo_slots: number;
  real_slots: number;
  single_license: boolean;
  demo_approval: DemoApproval;
  real_approval: RealApproval;
  real_auto_delay_hours: number | null;
  real_auto_delay_minutes: number | null;
  licensing_mode: LicensingMode;
  simulated: boolean;
  active: boolean;
  sort_order: number;
}

/** Campos editables de la config (PATCH parcial). */
export type RobotPlanConfigPatch = Partial<
  Pick<
    RobotPlanConfig,
    | 'demo_enabled'
    | 'real_enabled'
    | 'demo_slots'
    | 'real_slots'
    | 'single_license'
    | 'demo_approval'
    | 'real_approval'
    | 'real_auto_delay_hours'
    | 'real_auto_delay_minutes'
    | 'licensing_mode'
    | 'simulated'
    | 'active'
    | 'sort_order'
  >
>;

export const fetchRobotConfig = (a: AuthIdentity | null) =>
  getAdmin<HubListEnvelope<RobotPlanConfig>>(`${HUB}/robot-config`, a);

export const patchRobotConfig = (
  a: AuthIdentity | null,
  productId: string,
  patch: RobotPlanConfigPatch,
) =>
  patchAdmin<RobotPlanConfig>(
    `${HUB}/robot-config/${encodeURIComponent(productId)}`,
    a,
    patch,
  );

/** Estado de una activación gestionada por el campus. */
export type ActivationStatus = 'pending' | 'active' | 'revoked';
/** Tipo de cuenta de una activación. */
export type ActivationAccountType = 'demo' | 'real';

/** Una activación de robot de un alumno (campus.robot_activations). */
export interface RobotActivation {
  id: string;
  user_email: string;
  user_name: string | null;
  product_id: string;
  account_type: ActivationAccountType;
  account_number: number | null;
  broker_server: string | null;
  status: ActivationStatus;
  source: string;
  simulated: boolean;
  requested_at: string | null;
  approved_at: string | null;
  account_platform?: string | null;
  account_currency?: string | null;
  /** Fase 1 · pre-check IB: momento en que se confirmó la afiliación IB (API broker o staff). */
  ib_verified_at?: string | null;
  /** Quién verificó el IB: 'puprime-api' o email del staff. */
  ib_verified_by?: string | null;
}

export const fetchRobotActivations = (
  a: AuthIdentity | null,
  params: { status?: string; q?: string } = {},
) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getAdmin<HubListEnvelope<RobotActivation>>(`${HUB}/activations${suffix}`, a);
};

export const approveActivation = (a: AuthIdentity | null, id: string) =>
  postAdmin<RobotActivation>(`${HUB}/activations/${encodeURIComponent(id)}/approve`, a);

/** Fase 1 · marca "IB verificado" en una activación campus pendiente SIN aprobarla (VT/FP/Exness · manual). */
export const markActivationIbVerified = (a: AuthIdentity | null, id: string) =>
  postAdmin<RobotActivation>(`${HUB}/activations/${encodeURIComponent(id)}/mark-ib-verified`, a);

export const rejectActivation = (a: AuthIdentity | null, id: string) =>
  postAdmin<RobotActivation>(`${HUB}/activations/${encodeURIComponent(id)}/reject`, a);

export const revokeActivation = (a: AuthIdentity | null, id: string) =>
  postAdmin<RobotActivation>(`${HUB}/activations/${encodeURIComponent(id)}/revoke`, a);

/** Cuerpo de la solicitud de activación del alumno (POST /api/me/robots/request). */
export interface MyRobotRequestPayload {
  product_id: string;
  account_type: ActivationAccountType;
  account_number?: number;
  broker_server?: string;
  platform?: 'MT4' | 'MT5';
}

/** Solicitud de activación de robot del alumno · capa campus (NO proxy al core).
 *  Devuelve la fila creada con su `status` resuelto por la config del robot. */
export async function requestMyRobot(
  auth: AuthIdentity | null,
  payload: MyRobotRequestPayload,
): Promise<RobotActivation> {
  const res = await fetch(`${API_BASE}/me/robots/request`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(payload),
  });
  return handleResponse<RobotActivation>(res);
}

export interface AdminLicensingLicense {
  id: string | null;
  account_number: string | null;
  broker: string | null;
  platform: string | null;
  account_type: string | null;
  status: string | null;
  issued_at: string | null;
  last_seen_at: string | null;
}

export interface AdminLicensingRobot {
  product_id: string;
  product_label: string;
  slots_total: number;
  slots_used: number;
  slots_remaining: number;
  licenses: AdminLicensingLicense[];
}

export interface AdminLicensingAvailableRobot {
  product_id: string;
  product_label: string;
  purchased: boolean;
}

export interface AdminUserLicensing {
  user_id: string;
  user_email: string;
  licensing_unavailable: boolean;
  tier: string;
  robots: AdminLicensingRobot[];
  available_robots: AdminLicensingAvailableRobot[];
}

export async function fetchAdminUserLicensing(
  userId: string,
  auth: AuthIdentity | null,
): Promise<AdminUserLicensing> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/licensing`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AdminUserLicensing>(res);
}

export async function setAdminRobotVolume(
  userId: string,
  productId: string,
  qtyActivations: number,
  auth: AuthIdentity | null,
): Promise<unknown> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/robot-volume`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ product_id: productId, qty_activations: qtyActivations }),
  });
  return handleResponse(res);
}

// ── Acciones de gestión seguras (T5 · Admin v2) ──────────────────────────────
// resend-email · discord-resync · export CSV. Las dos primeras son best-effort
// en el backend (devuelven {ok, detail} con 200 aunque la integración esté OFF).

export async function resendUserEmail(
  userId: string,
  kind: 'welcome' | 'activation',
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; detail: string; kind: string }> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/resend-email`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ kind }),
  });
  return handleResponse<{ ok: boolean; detail: string; kind: string }>(res);
}

export async function discordResyncUser(
  userId: string,
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; detail: string; tier: string | null }> {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/discord-resync`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<{ ok: boolean; detail: string; tier: string | null }>(res);
}

/**
 * Descarga el CSV de usuarios (email, full_name, tier, status, role,
 * created_at, last_seen_at). Reusa `downloadAuthedFile`: pide el endpoint con
 * el JWT del admin y entrega el blob como descarga en el navegador.
 */
export async function exportAdminUsersCsv(auth: AuthIdentity | null): Promise<void> {
  await downloadAuthedFile(auth, `${API_BASE}/admin/users/export.csv`);
}

// ── Impersonación "ver como alumno" (F3 RBAC · read-only) ────────────────────
// start/stop SIEMPRE se autentican con la sesión REAL del admin (su JWT de
// Supabase), NUNCA con el token de impersonación: el de impersonación es
// read-only y bloquearía estos POST en el backend. Por eso construyen el header
// a mano en vez de usar buildHeaders() (que prioriza el token de impersonación).

function buildRealAdminHeaders(auth: AuthIdentity | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!auth) return headers;
  if (auth.mode === 'supabase' && auth.jwt) {
    headers['Authorization'] = `Bearer ${auth.jwt}`;
  } else if (auth.mode === 'stub' && isStubModeAllowed()) {
    headers['X-User-Email'] = auth.email;
  }
  return headers;
}

/**
 * Inicia una impersonación: pide al backend un token read-only para ver el
 * campus como el alumno `userId`. Devuelve el contrato completo (incl. token,
 * target, expires_at, jti). El caller debe persistir la sesión
 * (`setImpersonationFromResponse`) y refrescar la auth.
 */
export async function startImpersonation(
  userId: string,
  auth: AuthIdentity | null,
): Promise<ImpersonateStartResponse> {
  const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}/impersonate`, {
    method: 'POST',
    headers: buildRealAdminHeaders(auth),
  });
  return handleResponse<ImpersonateStartResponse>(res);
}

/**
 * Activa un plan (`tier`) en el ALUMNO DE PRUEBA del staff y entra como él.
 * Crea (idempotente) una cuenta de alumno REAL del staff (is_test), le concede
 * el plan acumulativo y devuelve un token ESCRIBIBLE (progreso real). Mismo shape
 * que `startImpersonation`. El caller persiste la sesión y refresca la auth.
 */
export async function activateTestStudent(
  tier: 'start' | 'basic' | 'pro',
  auth: AuthIdentity | null,
): Promise<ImpersonateStartResponse> {
  const res = await fetch(`${API_BASE}/admin/impersonate/test-student?tier=${tier}`, {
    method: 'POST',
    headers: buildRealAdminHeaders(auth),
  });
  return handleResponse<ImpersonateStartResponse>(res);
}

/**
 * Cierra el rastro de auditoría de una impersonación. Se autentica con la
 * sesión REAL del admin. `token` es el token de impersonación que se está
 * descartando (opcional · empareja start/stop por jti en auditoría).
 */
export async function stopImpersonation(
  token: string | null,
  auth: AuthIdentity | null,
): Promise<{ ok: boolean; jti: string | null }> {
  const res = await fetch(`${API_BASE}/admin/impersonate/stop`, {
    method: 'POST',
    headers: buildRealAdminHeaders(auth),
    body: JSON.stringify({ token }),
  });
  return handleResponse<{ ok: boolean; jti: string | null }>(res);
}

// ── Marketdata · Señales (Fase 1 · admin-only · feat/campus-marketdata) ─────
// GET /api/marketdata/signals/stats · winrate por ventana + señales recientes.
// El backend exige rol admin (403 en caso contrario).

export async function fetchSignalStats(
  auth: AuthIdentity | null,
): Promise<SignalStats> {
  const res = await fetch(`${API_BASE}/marketdata/signals/stats`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<SignalStats>(res);
}

// ── Robotea Lab · Informes (solo Jaime · robotea_lab.view) ──────────────────
// Duplica la vista del Hub dentro del campus. GET /api/robotea-lab/informes
// lista los robots publicados (espejo de informe.json); el privado.html se
// pinta en un iframe sandbox="" (mismo motivo que en el hub: srcDoc en vez de
// src, porque un iframe no manda el header Authorization).

export interface RoboteaLabInforme {
  key: string;
  robot: string;
  mercado: string;
  version: string | null;
  marco: string | null;
  ventana: string | null;
  meses: number | null;
  broker_test: string | null;
  estado: string | null;
  doc_version: string | null;
  publicado: string | null;
  url_publico: string | null;
  generado: string | null;
  /** "publicado" | "borrador" — espejo de `campus.estado` en la ficha del
   * pipeline (creator/lab-pipeline/robots/<key>.yaml). "borrador" = el
   * documento existe pero Jaime todavía no lo ha dado por terminado para
   * enseñarlo (va en "Informes en revisión", no en "Informes publicados"). */
  campus_estado: 'publicado' | 'borrador';
  tiene_privado: boolean;
  tiene_pdf: boolean;
  pdf_nombre: string | null;
  pdf_bytes: number | null;
  /** Grid de 3 estadísticas (Jaime, 30-ago) — delegado a Andrés (carril
   *  Robotea Lab Informes) añadirlos a `informe.json` desde
   *  `monkey-oro.yaml`: `niveles` (cuenta), `analitica.profit_factor`,
   *  `analitica.operaciones`. Opcionales: si el backend aún no los manda
   *  para un robot, la UI no rompe, solo no pinta esa celda. */
  niveles_riesgo?: number | null;
  profit_factor?: number | null;
  n_operaciones?: number | null;
  /** Carátula real del robot (`/robots/<archivo>.png`), añadida en
   *  `publicar_hub.py` desde `meta.cover` de la ficha. `null` si la ficha
   *  no la declara — el frontend cae al placeholder con inicial. */
  cover?: string | null;
}

export interface RoboteaLabInformesResponse {
  informes: RoboteaLabInforme[];
}

export async function fetchRoboteaLabInformes(
  auth: AuthIdentity | null,
): Promise<RoboteaLabInformesResponse> {
  const res = await fetch(`${API_BASE}/robotea-lab/informes`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<RoboteaLabInformesResponse>(res);
}

export async function fetchRoboteaLabInformePrivado(
  auth: AuthIdentity | null,
  key: string,
): Promise<string> {
  const res = await fetch(`${API_BASE}/robotea-lab/informes/${encodeURIComponent(key)}/privado`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  if (!res.ok) {
    const err: ApiError = { detail: `HTTP ${res.status}`, status: res.status };
    throw err;
  }
  return res.text();
}

export async function descargarRoboteaLabInformePdf(
  auth: AuthIdentity | null,
  key: string,
): Promise<void> {
  await downloadAuthedFile(auth, `${API_BASE}/robotea-lab/informes/${encodeURIComponent(key)}/pdf`);
}

// ── Marketdata · Briefing (Fase 2 · Mercado · Basic/Pro) ────────────────────
// GET /api/marketdata/briefing/today · briefing diario. El backend exige tier
// Basic/Pro (403 para Start). `briefing` puede ser null (sin briefing aún).

export async function fetchBriefingToday(
  auth: AuthIdentity | null,
): Promise<BriefingTodayResponse> {
  const res = await fetch(`${API_BASE}/marketdata/briefing/today`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<BriefingTodayResponse>(res);
}

// ── Calendario económico nativo (alto impacto · ForexFactory · ES) ──────────
export interface EconCalEvent {
  time: string;
  currency: string;
  impact: string;
  title: string;
  forecast: string;
  previous: string;
  actual: string;
}

export interface EconCalDay {
  date: string;
  label: string;
  events: EconCalEvent[];
}

export interface EconCalResponse {
  source: string;
  generated_at: string;
  fallback: boolean;
  fallback_url?: string;
  days: EconCalDay[];
}

// GET /api/marketdata/calendar · calendario alto impacto nativo (ES).
export async function fetchEconomicCalendar(
  auth: AuthIdentity | null,
): Promise<EconCalResponse> {
  const res = await fetch(`${API_BASE}/marketdata/calendar`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<EconCalResponse>(res);
}

// GET /api/marketdata/briefing/history · histórico ligero (sin payload).
export async function fetchBriefingHistory(
  auth: AuthIdentity | null,
): Promise<BriefingHistoryResponse> {
  const res = await fetch(`${API_BASE}/marketdata/briefing/history`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<BriefingHistoryResponse>(res);
}

// ── Achievements / Resources / Notes (A2) ──────────────────────────────────

export async function fetchAchievements(auth: AuthIdentity | null): Promise<AchievementsResponse> {
  const res = await fetch(`${API_BASE}/me/achievements`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<AchievementsResponse>(res);
}

export async function fetchResources(auth: AuthIdentity | null): Promise<ResourcesResponse> {
  const res = await fetch(`${API_BASE}/me/resources`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<ResourcesResponse>(res);
}

export async function fetchNote(
  lessonId: string,
  auth: AuthIdentity | null,
): Promise<LessonNote | null> {
  const res = await fetch(`${API_BASE}/me/notes/${encodeURIComponent(lessonId)}`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  if (res.status === 404) return null;
  return handleResponse<LessonNote>(res);
}

export async function saveNote(
  lessonId: string,
  body: string,
  auth: AuthIdentity | null,
): Promise<LessonNote> {
  const res = await fetch(`${API_BASE}/me/notes/${encodeURIComponent(lessonId)}`, {
    method: 'PUT',
    headers: buildHeaders(auth),
    body: JSON.stringify({ body }),
  });
  return handleResponse<LessonNote>(res);
}

// ── Course Bonuses (F-COURSE-BONUSES-001 · recovered 2026-05-27) ─────────

export async function fetchCourseBonuses(
  courseSlug: string,
  auth: AuthIdentity | null,
): Promise<CourseBonusesResponse> {
  const res = await fetch(
    `${API_BASE}/courses/${encodeURIComponent(courseSlug)}/bonuses`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<CourseBonusesResponse>(res);
}

export async function fetchBonus(
  courseSlug: string,
  bonusSlug: string,
  auth: AuthIdentity | null,
): Promise<CourseBonus> {
  const res = await fetch(
    `${API_BASE}/courses/${encodeURIComponent(courseSlug)}/bonuses/${encodeURIComponent(bonusSlug)}`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<CourseBonus>(res);
}

export async function fetchMyBonusProgress(
  auth: AuthIdentity | null,
): Promise<BonusProgressListResponse> {
  const res = await fetch(`${API_BASE}/me/bonuses/progress`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<BonusProgressListResponse>(res);
}

export async function toggleBonusChecklist(
  bonusId: string,
  itemId: string,
  checked: boolean,
  auth: AuthIdentity | null,
): Promise<BonusProgress & { bonus_id: string }> {
  const res = await fetch(
    `${API_BASE}/me/bonuses/${encodeURIComponent(bonusId)}/checklist`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ item_id: itemId, checked }),
    },
  );
  return handleResponse<BonusProgress & { bonus_id: string }>(res);
}

export async function saveBonusCalculator(
  bonusId: string,
  state: Record<string, number>,
  auth: AuthIdentity | null,
): Promise<BonusProgress & { bonus_id: string }> {
  const res = await fetch(
    `${API_BASE}/me/bonuses/${encodeURIComponent(bonusId)}/calculator`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ state }),
    },
  );
  return handleResponse<BonusProgress & { bonus_id: string }>(res);
}

export async function completeBonus(
  bonusId: string,
  auth: AuthIdentity | null,
): Promise<BonusProgress & { bonus_id: string }> {
  const res = await fetch(
    `${API_BASE}/me/bonuses/${encodeURIComponent(bonusId)}/complete`,
    { method: 'POST', headers: buildHeaders(auth) },
  );
  return handleResponse<BonusProgress & { bonus_id: string }>(res);
}

export async function uncompleteBonus(
  bonusId: string,
  auth: AuthIdentity | null,
): Promise<BonusProgress & { bonus_id: string }> {
  const res = await fetch(
    `${API_BASE}/me/bonuses/${encodeURIComponent(bonusId)}/uncomplete`,
    { method: 'POST', headers: buildHeaders(auth) },
  );
  return handleResponse<BonusProgress & { bonus_id: string }>(res);
}

// ── Module video progress (Start sin lessons · sprint module-video 2026-05-27) ─

export interface ModuleVideoProgressResponse {
  module_slug: string;
  video_completed_at: string | null;
  checklist_state: Record<string, boolean>;
  completed_at: string | null;
  all_checked: boolean;
  was_just_completed?: boolean;
}

export async function markModuleVideoWatched(
  slug: string,
  auth: AuthIdentity | null,
): Promise<ModuleVideoProgressResponse> {
  const res = await fetch(`${API_BASE}/modules/${encodeURIComponent(slug)}/video-watched`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<ModuleVideoProgressResponse>(res);
}

export async function unmarkModuleVideoWatched(
  slug: string,
  auth: AuthIdentity | null,
): Promise<ModuleVideoProgressResponse> {
  const res = await fetch(`${API_BASE}/modules/${encodeURIComponent(slug)}/video-unwatched`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<ModuleVideoProgressResponse>(res);
}

// ── Lesson progress (sprint lesson-progress 2026-05-27) ───────────────────

export async function completeLesson(
  lessonId: string,
  auth: AuthIdentity | null,
): Promise<LessonProgressResponse> {
  const res = await fetch(`${API_BASE}/me/lessons/${encodeURIComponent(lessonId)}/complete`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<LessonProgressResponse>(res);
}

export async function uncompleteLesson(
  lessonId: string,
  auth: AuthIdentity | null,
): Promise<LessonProgressResponse> {
  const res = await fetch(`${API_BASE}/me/lessons/${encodeURIComponent(lessonId)}/uncomplete`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<LessonProgressResponse>(res);
}

export async function markLessonVideoWatched(
  lessonId: string,
  auth: AuthIdentity | null,
): Promise<LessonProgressResponse> {
  const res = await fetch(`${API_BASE}/me/lessons/${encodeURIComponent(lessonId)}/video-watched`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<LessonProgressResponse>(res);
}

export async function patchLessonChecklist(
  lessonId: string,
  itemId: string,
  checked: boolean,
  auth: AuthIdentity | null,
): Promise<LessonProgressResponse> {
  const res = await fetch(`${API_BASE}/me/lessons/${encodeURIComponent(lessonId)}/checklist`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify({ item_id: itemId, checked }),
  });
  return handleResponse<LessonProgressResponse>(res);
}

// ── Comments (community activation 2026-05-27 · co-owned Andrés+Carlos) ────

export const COMMENT_BODY_MAX = 1000;

export async function fetchComments(
  slug: string,
  auth: AuthIdentity | null,
): Promise<CommentsResponse> {
  const res = await fetch(
    `${API_BASE}/modules/${encodeURIComponent(slug)}/comments`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<CommentsResponse>(res);
}

export async function postComment(
  slug: string,
  body: string,
  parentId: number | null,
  auth: AuthIdentity | null,
): Promise<ModuleComment> {
  const res = await fetch(
    `${API_BASE}/modules/${encodeURIComponent(slug)}/comments`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ body, parent_id: parentId }),
    },
  );
  return handleResponse<ModuleComment>(res);
}

// ── Lesson-level comments (feat/lesson-comments · 2026-05-27 · Andrés Mora) ──
//
// Para módulos con múltiples lecciones (Basic/Pro), cada caja de comentarios
// vive bajo el alias `/api/lessons/{lessonId}/comments` · el módulo Start
// (sin lessons) sigue usando `/api/modules/{slug}/comments`.

export async function fetchLessonComments(
  lessonId: string,
  auth: AuthIdentity | null,
): Promise<CommentsResponse> {
  const res = await fetch(
    `${API_BASE}/lessons/${encodeURIComponent(lessonId)}/comments`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<CommentsResponse>(res);
}

export async function postLessonComment(
  lessonId: string,
  body: string,
  parentId: number | null,
  auth: AuthIdentity | null,
): Promise<ModuleComment> {
  const res = await fetch(
    `${API_BASE}/lessons/${encodeURIComponent(lessonId)}/comments`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ body, parent_id: parentId }),
    },
  );
  return handleResponse<ModuleComment>(res);
}

export async function deleteComment(
  commentId: number,
  auth: AuthIdentity | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/comments/${commentId}`, {
    method: 'DELETE',
    headers: buildHeaders(auth),
  });
  if (!res.ok && res.status !== 204) {
    await handleResponse<unknown>(res);
  }
}

export async function editComment(
  commentId: number,
  body: string,
  auth: AuthIdentity | null,
): Promise<ModuleComment> {
  const res = await fetch(`${API_BASE}/comments/${commentId}`, {
    method: 'PUT',
    headers: buildHeaders(auth),
    body: JSON.stringify({ body }),
  });
  return handleResponse<ModuleComment>(res);
}

export async function likeComment(
  commentId: number,
  auth: AuthIdentity | null,
): Promise<CommentLikeResponse> {
  const res = await fetch(`${API_BASE}/comments/${commentId}/like`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<CommentLikeResponse>(res);
}

export async function unlikeComment(
  commentId: number,
  auth: AuthIdentity | null,
): Promise<CommentLikeResponse> {
  const res = await fetch(`${API_BASE}/comments/${commentId}/like`, {
    method: 'DELETE',
    headers: buildHeaders(auth),
  });
  return handleResponse<CommentLikeResponse>(res);
}

export async function fetchRecentComments(
  limit: number,
  auth: AuthIdentity | null,
): Promise<RecentCommentsResponse> {
  const safe = Math.max(1, Math.min(limit, 50));
  const res = await fetch(`${API_BASE}/comments/recent?limit=${safe}`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<RecentCommentsResponse>(res);
}

// ─── Courses (multi-curso · sprint 2026-05-27) ──────────────────────────────

export async function fetchCourses(
  auth: AuthIdentity | null,
): Promise<CoursesListResponse> {
  const res = await fetch(`${API_BASE}/courses`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<CoursesListResponse>(res);
}

export async function fetchCourseDetail(
  slug: string,
  auth: AuthIdentity | null,
): Promise<CourseDetail> {
  const res = await fetch(`${API_BASE}/courses/${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<CourseDetail>(res);
}

export async function fetchMyCourses(
  auth: AuthIdentity | null,
): Promise<MyCoursesResponse> {
  const res = await fetch(`${API_BASE}/me/courses`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<MyCoursesResponse>(res);
}

// Certificados PDF (sprint campus-wave2 · 2026-05-27)
// Descarga el certificado PDF como blob y devuelve filename + bytes.
// Llama window.URL.createObjectURL en el caller para disparar save-as.

export async function fetchCertificate(
  courseSlug: string,
  auth: AuthIdentity | null,
): Promise<{ ok: true; filename: string; blob: Blob }> {
  const res = await fetch(
    `${API_BASE}/me/certificate/${encodeURIComponent(courseSlug)}`,
    {
      method: 'GET',
      headers: buildHeaders(auth),
    },
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // ignore json parse
    }
    const err: ApiError = { detail, status: res.status };
    throw err;
  }
  const cd = res.headers.get('content-disposition') || '';
  const matchResult = /filename="?([^";]+)"?/i.exec(cd);
  const filename = matchResult?.[1] ?? `certificado-robotea-${courseSlug}.pdf`;
  const blob = await res.blob();
  return { ok: true, filename, blob };
}

export async function enrollInCourse(
  slug: string,
  auth: AuthIdentity | null,
): Promise<EnrollCourseResponse> {
  const res = await fetch(
    `${API_BASE}/me/courses/${encodeURIComponent(slug)}/enroll`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
    },
  );
  return handleResponse<EnrollCourseResponse>(res);
}

export async function requestCourseNotification(
  slug: string,
  auth: AuthIdentity | null,
): Promise<NotifyCourseResponse> {
  const res = await fetch(
    `${API_BASE}/me/courses/${encodeURIComponent(slug)}/notify`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
    },
  );
  return handleResponse<NotifyCourseResponse>(res);
}

// ── Onboarding wizard (Wave 2) ────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  /** Campo opcional · presente en perfiles con número de WhatsApp guardado. */
  whatsapp?: string | null;
  source: string;
  role: CampusRole;
  activated_at: string | null;
  last_seen_at: string | null;
  created_at: string | null;
  onboarding_completed_at: string | null;
  onboarding_dismissed_at: string | null;
  first_login_at: string | null;
  // ── Demo-start fields (Task 2 · feat/demo-start-access) ─────────────────────
  /** Plan de demo activo ('demo-start') o null si el usuario no es demo. */
  demo_plan?: string | null;
  /** True si el demo está activo (plan set + no expirado + no bloqueado). */
  is_active_demo?: boolean;
  /** ISO timestamp de cuándo se bloqueó el acceso demo (null = aún activo). */
  demo_locked_at?: string | null;
  /** ISO timestamp de cuándo expira el periodo demo. */
  demo_expires_at?: string | null;
  /** ISO timestamp de cuándo completó el onboarding demo (null = pendiente). */
  demo_onboarding_completed_at?: string | null;
}

export async function fetchProfile(auth: AuthIdentity | null): Promise<UserProfile> {
  const res = await fetch(`${API_BASE}/me/profile`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<UserProfile>(res);
}

async function postOnboardingAction(
  action: 'complete' | 'dismiss' | 'reset',
  auth: AuthIdentity | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/me/onboarding/${action}`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  if (!res.ok && res.status !== 204) {
    await handleResponse<unknown>(res);
  }
}

export const completeOnboarding = (auth: AuthIdentity | null): Promise<void> =>
  postOnboardingAction('complete', auth);

export const dismissOnboarding = (auth: AuthIdentity | null): Promise<void> =>
  postOnboardingAction('dismiss', auth);

export const resetOnboarding = (auth: AuthIdentity | null): Promise<void> =>
  postOnboardingAction('reset', auth);

// ── Notifications (bell + inbox · sprint campus-notifications 2026-05-27) ──

export async function fetchNotifications(
  unreadOnly: boolean,
  limit: number,
  auth: AuthIdentity | null,
): Promise<NotificationsResponse> {
  const safeLimit = Math.max(1, Math.min(limit, 100));
  const params = new URLSearchParams({
    unread_only: unreadOnly ? 'true' : 'false',
    limit: String(safeLimit),
  });
  const res = await fetch(`${API_BASE}/me/notifications?${params.toString()}`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<NotificationsResponse>(res);
}

export async function markNotificationRead(
  id: string,
  auth: AuthIdentity | null,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/me/notifications/${encodeURIComponent(id)}/read`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
    },
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // ignore
    }
    const err: ApiError = { detail, status: res.status };
    throw err;
  }
}

export async function markAllNotificationsRead(
  auth: AuthIdentity | null,
): Promise<MarkAllReadResponse> {
  const res = await fetch(`${API_BASE}/me/notifications/read-all`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<MarkAllReadResponse>(res);
}

export async function deleteNotification(
  id: string,
  auth: AuthIdentity | null,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/me/notifications/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: buildHeaders(auth),
    },
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // ignore
    }
    const err: ApiError = { detail, status: res.status };
    throw err;
  }
}

// ── AI Tutor sidebar (sprint campus-ia-tutor · 2026-05-27) ─────────────────
// Backend en /api/me/tutor/* hace de proxy + persistence layer al RAG vps-go.
// Max 500 chars por pregunta · max 20 preguntas/hora (backend rate-limit 429).

export const TUTOR_QUESTION_MAX_CHARS = 500;

export async function createTutorSession(
  moduleSlug: string | null,
  auth: AuthIdentity | null,
): Promise<TutorSession> {
  const res = await fetch(`${API_BASE}/me/tutor/sessions`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ module_slug: moduleSlug ?? null }),
  });
  return handleResponse<TutorSession>(res);
}

export async function listTutorSessions(
  auth: AuthIdentity | null,
): Promise<TutorSessionsResponse> {
  const res = await fetch(`${API_BASE}/me/tutor/sessions`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<TutorSessionsResponse>(res);
}

export async function fetchTutorMessages(
  sessionId: string,
  auth: AuthIdentity | null,
): Promise<TutorMessagesResponse> {
  const res = await fetch(
    `${API_BASE}/me/tutor/sessions/${encodeURIComponent(sessionId)}/messages`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<TutorMessagesResponse>(res);
}

/** Registra si la consulta quedó resuelta (Sí/No) · señal para analítica interna. */
export async function submitTutorFeedback(
  sessionId: string,
  resolved: boolean,
  auth: AuthIdentity | null,
): Promise<TutorSession> {
  const res = await fetch(
    `${API_BASE}/me/tutor/sessions/${encodeURIComponent(sessionId)}/feedback`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ resolved }),
    },
  );
  return handleResponse<TutorSession>(res);
}

export async function sendTutorMessage(
  sessionId: string,
  question: string,
  auth: AuthIdentity | null,
): Promise<TutorSendResponse> {
  const res = await fetch(
    `${API_BASE}/me/tutor/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ question }),
    },
  );
  return handleResponse<TutorSendResponse>(res);
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'detail' in value &&
    'status' in value &&
    typeof (value as { detail: unknown }).detail === 'string'
  );
}

// ── Discord auto-link tier-sync (sprint feat/discord-auto-link · 2026-05-28) ──
// Vincular cuenta Discord del alumno + auto-rol según tier (Start/Basic/Pro).
// Server Discord PRIVADO con canales por rol (Circle/Skool pattern).

export async function discordLinkInit(
  auth: AuthIdentity | null,
): Promise<DiscordLinkInitResponse> {
  const res = await fetch(`${API_BASE}/me/discord/link/init`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<DiscordLinkInitResponse>(res);
}

export async function discordLinkStatus(
  auth: AuthIdentity | null,
): Promise<DiscordLinkStatusResponse> {
  const res = await fetch(`${API_BASE}/me/discord/status`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<DiscordLinkStatusResponse>(res);
}

export async function discordLinkSync(
  auth: AuthIdentity | null,
): Promise<DiscordLinkSyncResponse> {
  const res = await fetch(`${API_BASE}/me/discord/sync`, {
    method: 'POST',
    headers: buildHeaders(auth),
  });
  return handleResponse<DiscordLinkSyncResponse>(res);
}

export async function discordLinkRemove(
  auth: AuthIdentity | null,
): Promise<DiscordLinkUnlinkResponse> {
  const res = await fetch(`${API_BASE}/me/discord/link`, {
    method: 'DELETE',
    headers: buildHeaders(auth),
  });
  return handleResponse<DiscordLinkUnlinkResponse>(res);
}

// ── Discord chat embedded relay (sprint feat/discord-chat-embedded-relay · 2026-05-28) ──

export interface FetchChatMessagesOpts {
  limit?: number;
  before?: string | null;
}

export async function fetchDiscordChatMessages(
  auth: AuthIdentity | null,
  channelId: string,
  opts: FetchChatMessagesOpts = {},
): Promise<DiscordChatMessagesResponse> {
  const params = new URLSearchParams({ channel_id: channelId });
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.before) params.set('before', opts.before);
  const res = await fetch(
    `${API_BASE}/me/discord/chat/messages?${params.toString()}`,
    { method: 'GET', headers: buildHeaders(auth) },
  );
  return handleResponse<DiscordChatMessagesResponse>(res);
}

export async function postDiscordChatMessage(
  auth: AuthIdentity | null,
  channelId: string,
  content: string,
  replyToId?: string | null,
): Promise<DiscordChatPostResponse> {
  const body: Record<string, unknown> = { channel_id: channelId, content };
  if (replyToId) body.reply_to_id = replyToId;
  const res = await fetch(`${API_BASE}/me/discord/chat/messages`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(body),
  });
  return handleResponse<DiscordChatPostResponse>(res);
}

/**
 * Edit propio mensaje del chat (sprint feat/discord-chat-edit-delete-own).
 * Backend exige ownership server-side (`campus_user_id == user.id`) · 403
 * si no eres el autor, 404 si no existe o ya está borrado.
 */
export async function editDiscordChatMessage(
  auth: AuthIdentity | null,
  messageId: string,
  content: string,
): Promise<DiscordChatEditResponse> {
  const res = await fetch(
    `${API_BASE}/me/discord/chat/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'PATCH',
      headers: buildHeaders(auth),
      body: JSON.stringify({ content }),
    },
  );
  return handleResponse<DiscordChatEditResponse>(res);
}

/**
 * Soft-delete propio mensaje del chat (sprint feat/discord-chat-edit-delete-own).
 * 204 No Content si todo OK · 403 si no eres el autor · 404 si ya borrado.
 */
export async function deleteDiscordChatMessage(
  auth: AuthIdentity | null,
  messageId: string,
): Promise<{ ok: true }> {
  const res = await fetch(
    `${API_BASE}/me/discord/chat/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'DELETE',
      headers: buildHeaders(auth),
    },
  );
  if (res.ok || res.status === 204) {
    return { ok: true };
  }
  // Reusa handleResponse para mantener ApiError shape coherente.
  await handleResponse<unknown>(res);
  return { ok: true };
}

interface DiscordChatStreamTicketResponse {
  ticket: string;
  expires_in_seconds: number;
}

/**
 * Mint short-lived single-use ticket para auth del SSE stream.
 *
 * Security HIGH fix: el JWT NO puede ir en URL del EventSource (filtra a
 * nginx logs · browser history · referrer · proxies). Pattern ticket:
 * POST aquí con JWT en header Authorization Bearer → backend devuelve ticket
 * single-use TTL 60s · frontend lo pone como ?ticket=X en EventSource.
 */
export async function mintDiscordChatStreamTicket(
  auth: AuthIdentity | null,
  channelId: string,
): Promise<DiscordChatStreamTicketResponse> {
  const res = await fetch(`${API_BASE}/me/discord/chat/stream-ticket`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ channel_id: channelId }),
  });
  return handleResponse<DiscordChatStreamTicketResponse>(res);
}

/**
 * Suscribe a SSE stream del canal · cada nuevo evento Discord llega como callback.
 * Devuelve función `unsubscribe` para cleanup. EventSource ausente → no-op.
 *
 * Async porque internamente hace mint del ticket short-lived antes de abrir
 * el EventSource (security HIGH fix · ver `mintDiscordChatStreamTicket`).
 * El JWT/email NUNCA viajan en la URL.
 *
 * Backlog F-CHAT-SSE-AUTH-FORMAL: explorar cookie httpOnly como alternativa
 * más fuerte (requiere CORS withCredentials + Set-Cookie config nginx).
 */
export async function subscribeDiscordChat(
  auth: AuthIdentity | null,
  channelId: string,
  onEvent: (event: DiscordChatStreamEvent) => void,
  onError?: (err: Event) => void,
): Promise<() => void> {
  if (typeof EventSource === 'undefined' || !auth) {
    return () => {};
  }
  let ticket: string;
  try {
    const minted = await mintDiscordChatStreamTicket(auth, channelId);
    ticket = minted.ticket;
  } catch (err) {
    if (onError) {
      const ev = new ErrorEvent('error', {
        message: err instanceof Error ? err.message : 'ticket mint failed',
      });
      onError(ev);
    }
    return () => {};
  }

  const params = new URLSearchParams({
    channel_id: channelId,
    ticket,
  });
  const url = `${API_BASE}/me/discord/chat/stream?${params.toString()}`;
  // NO withCredentials · NO cookies · NO JWT en URL · solo ticket short-lived.
  const source = new EventSource(url);

  const handler = (ev: MessageEvent): void => {
    try {
      const parsed = JSON.parse(ev.data) as DiscordChatStreamEvent;
      onEvent(parsed);
    } catch {
      /* ignore */
    }
  };

  source.addEventListener('message.create', handler);
  source.addEventListener('message.update', handler);
  source.addEventListener('message.delete', handler);
  source.addEventListener('message', handler);
  if (onError) source.addEventListener('error', onError);

  return () => {
    source.removeEventListener('message.create', handler);
    source.removeEventListener('message.update', handler);
    source.removeEventListener('message.delete', handler);
    source.removeEventListener('message', handler);
    if (onError) source.removeEventListener('error', onError);
    source.close();
  };
}

export type { DiscordChatMessage };

// ── Portal Tienda de Robot EAs (sprint 2026-05-28) ──────────────────────────

export interface StoreProduct {
  id: string;
  name: string;
}

export interface StoreRequestPayload {
  customer_name: string;
  product_id: string;
  account_number: number;
  broker_server: string;
  account_platform: 'MT4' | 'MT5';
  account_type: 'real' | 'demo';
  customer_telegram?: string;
  notes?: string;
}

export async function fetchStoreProducts(
  auth: AuthIdentity | null,
): Promise<StoreProduct[]> {
  const res = await fetch(`${API_BASE}/portal/store/products`, {
    method: 'GET',
    headers: buildHeaders(auth),
  });
  return handleResponse<StoreProduct[]>(res);
}

export async function createStoreRequest(
  auth: AuthIdentity | null,
  payload: StoreRequestPayload,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/portal/store/request`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify(payload),
  });
  return handleResponse<Record<string, unknown>>(res);
}

// ── Lista de espera (waitlist · Club RoboTeam + futuros topics) ──────────────
// POST /api/me/waitlist · requiere auth. Idempotente en el backend: si el
// alumno ya estaba apuntado devuelve 200 igual → la UI muestra éxito.

export interface WaitlistResponse {
  status: string;
}

export async function joinWaitlist(
  auth: AuthIdentity | null,
  topic: string,
): Promise<WaitlistResponse> {
  const res = await fetch(`${API_BASE}/me/waitlist`, {
    method: 'POST',
    headers: buildHeaders(auth),
    body: JSON.stringify({ topic }),
  });
  return handleResponse<WaitlistResponse>(res);
}

/** ¿Ya está el usuario en la lista de espera de `topic`? Lo lee el banner al
 *  cargar para mostrar 'Estás en la lista' de forma PERSISTENTE (no se pierde al
 *  refrescar). */
export async function getWaitlistStatus(
  auth: AuthIdentity | null,
  topic: string,
): Promise<{ topic: string; joined: boolean }> {
  const res = await fetch(`${API_BASE}/me/waitlist?topic=${encodeURIComponent(topic)}`, {
    headers: buildHeaders(auth),
  });
  return handleResponse<{ topic: string; joined: boolean }>(res);
}

/** Solicitudes de lista de espera (admin). Gated server-side por usuarios.view. */
export interface AdminWaitlistRequest {
  email: string;
  full_name: string | null;
  role: string | null;
  in_campus: boolean;
  joined_at: string | null;
  synced_to_ghl: boolean;
}
export async function fetchAdminWaitlist(
  auth: AuthIdentity | null,
  topic = 'roboteam',
): Promise<{ topic: string; total: number; requests: AdminWaitlistRequest[] }> {
  const res = await fetch(`${API_BASE}/admin/waitlist?topic=${encodeURIComponent(topic)}`, {
    headers: buildHeaders(auth),
  });
  return handleResponse<{ topic: string; total: number; requests: AdminWaitlistRequest[] }>(res);
}

// ── Enlaces externos · inventario editable (admin) · mig 072 ────────────────
export interface ExternalLink {
  id: string;
  link_key: string;
  grupo: string;
  label: string;
  url: string;
  activo: boolean;
  notas: string | null;
  sort_order: number;
  updated_at: string | null;
  updated_by: string | null;
}

export interface ExternalLinkUpdate {
  url?: string;
  label?: string;
  activo?: boolean;
  notas?: string | null;
}

/** Lista TODOS los enlaces externos (admin · gated integraciones). */
export async function fetchAdminLinks(auth: AuthIdentity | null): Promise<{ links: ExternalLink[] }> {
  const res = await fetch(`${API_BASE}/admin/links`, { headers: buildHeaders(auth) });
  return handleResponse<{ links: ExternalLink[] }>(res);
}

/** Actualiza un enlace externo (PATCH parcial). */
export async function updateAdminLink(
  auth: AuthIdentity | null,
  id: string,
  patch: ExternalLinkUpdate,
): Promise<ExternalLink> {
  const res = await fetch(`${API_BASE}/admin/links/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: buildHeaders(auth),
    body: JSON.stringify(patch),
  });
  return handleResponse<ExternalLink>(res);
}

/** Mapa {link_key: url} de enlaces externos ACTIVOS (inventario admin · mig 072).
 *  Lo consume el frontend (Fase 2) para que los CTAs tomen la URL gestionada
 *  desde el panel admin, con fallback al hardcode si falta o falla. */
export async function fetchPublicLinks(
  auth: AuthIdentity | null,
): Promise<{ links: Record<string, string> }> {
  const res = await fetch(`${API_BASE}/links`, { headers: buildHeaders(auth) });
  return handleResponse<{ links: Record<string, string> }>(res);
}
